import { Injectable, Signal, WritableSignal, inject, signal } from '@angular/core';
import { Client, IMessage, StompSubscription } from '@stomp/stompjs';
import { StompAccessTokenService } from './stomp-access-token.service';
import { stompBrokerUrl, withBearerHeader } from './stomp-broker';

/** Ein gerade im Modul-Drawer präsenter Nutzer, geschlüsselt auf tenant_users.id. */
export interface PresentUser {
  tenantUserId: string;
  displayName: string;
}

const HEARTBEAT_MS = 30_000;

/**
 * WebSocket/STOMP-Client für die Live-Präsenz im Modul-Edit-Drawer (NEX-692). Beim Öffnen
 * eines Moduls tritt der Nutzer der Präsenz bei und abonniert das Topic; alle Beteiligten
 * sehen sich gegenseitig. Ein Heartbeat hält den Eintrag am Leben; beim Schließen verlässt
 * der Nutzer die Präsenz. Die eigentliche Anzeige (Banner) liegt im Drawer.
 */
@Injectable({ providedIn: 'root' })
export class ModulePresenceService {
  private readonly accessToken = inject(StompAccessTokenService);

  /** Überschreibbarer Seam für Tests — echte Verbindung nur im Betrieb. */
  clientFactory: (brokerUrl: string) => Client = (brokerUrl) =>
    new Client({ brokerURL: brokerUrl, reconnectDelay: 5_000 });

  /** Liefert das aktuelle Access-Token für die STOMP-CONNECT-Header (Test-Seam). */
  tokenProvider: () => Promise<string | null> = () => this.accessToken.acquire();

  private client: Client | null = null;
  private readonly presenceMap = new Map<string, WritableSignal<PresentUser[]>>();
  private readonly subscriptions = new Map<string, StompSubscription>();
  private readonly heartbeats = new Map<string, ReturnType<typeof setInterval>>();

  /** Reaktive Präsenzliste eines Moduls (leere Liste, solange nichts empfangen wurde). */
  presence(moduleId: string): Signal<PresentUser[]> {
    return this.entry(moduleId);
  }

  join(moduleId: string, user: PresentUser): void {
    const client = this.ensureClient();
    const run = () => {
      this.subscribe(moduleId);
      this.publish(`/app/modules/${moduleId}/presence/join`, user);
      this.startHeartbeat(moduleId, user);
    };
    if (client.connected) {
      run();
    } else {
      const previous = client.onConnect;
      client.onConnect = (frame) => {
        previous?.(frame);
        run();
      };
    }
  }

  leave(moduleId: string, user: PresentUser): void {
    this.stopHeartbeat(moduleId);
    this.publish(`/app/modules/${moduleId}/presence/leave`, user);
    this.subscriptions.get(moduleId)?.unsubscribe();
    this.subscriptions.delete(moduleId);
    this.entry(moduleId).set([]);
  }

  /** Verarbeitet eine empfangene Präsenzliste (auch direkt aus Tests aufrufbar). */
  applyPresence(moduleId: string, users: PresentUser[]): void {
    this.entry(moduleId).set(users);
  }

  private subscribe(moduleId: string): void {
    if (this.subscriptions.has(moduleId) || !this.client) {
      return;
    }
    const sub = this.client.subscribe(`/topic/modules/${moduleId}/presence`, (message: IMessage) => {
      try {
        this.applyPresence(moduleId, JSON.parse(message.body) as PresentUser[]);
      } catch {
        this.applyPresence(moduleId, []);
      }
    });
    this.subscriptions.set(moduleId, sub);
  }

  private publish(destination: string, body: PresentUser): void {
    if (this.client?.connected) {
      this.client.publish({ destination, body: JSON.stringify(body) });
    }
  }

  private startHeartbeat(moduleId: string, user: PresentUser): void {
    this.stopHeartbeat(moduleId);
    const handle = setInterval(() => this.publish(`/app/modules/${moduleId}/presence/heartbeat`, user), HEARTBEAT_MS);
    this.heartbeats.set(moduleId, handle);
  }

  private stopHeartbeat(moduleId: string): void {
    const handle = this.heartbeats.get(moduleId);
    if (handle) {
      clearInterval(handle);
      this.heartbeats.delete(moduleId);
    }
  }

  private ensureClient(): Client {
    if (!this.client) {
      const client = this.clientFactory(stompBrokerUrl());
      withBearerHeader(client, () => this.tokenProvider());
      this.client = client;
      client.activate();
    }
    return this.client;
  }

  private entry(moduleId: string): WritableSignal<PresentUser[]> {
    let entry = this.presenceMap.get(moduleId);
    if (!entry) {
      entry = signal<PresentUser[]>([]);
      this.presenceMap.set(moduleId, entry);
    }
    return entry;
  }
}
